import { isRecord } from '../mappers';
import { type AuditMessage, auditMessage } from '../text';
import type { AuditLogEntry } from '../types';
import { auditValueText, toAuditValue } from '../values';

import { firstTwo, SUMMARY_VALUE_LENGTH, textField } from './lines';
import type { AuditDiffPair, AuditSummary } from './types';

function isEmptyRecord(value: Record<string, unknown>): boolean {
    return Object.keys(value).length === 0;
}

function sameJson(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((item, index) => sameJson(item, b[index]));
    }
    if (isRecord(a) && isRecord(b)) {
        const keys = Object.keys(a);
        return (
            keys.length === Object.keys(b).length &&
            keys.every((key) => key in b && sameJson(a[key], b[key]))
        );
    }
    return false;
}

/**
 * Every field of a before/after pair, in the order the entry lists them. Some writers
 * record whole objects rather than only the changed keys (a bioscope placement keeps
 * its unchanged contract), so each pair says whether it changed.
 */
export function changePairs(
    before: Record<string, unknown>,
    after: Record<string, unknown>
): AuditDiffPair[] {
    const fields = [...new Set([...Object.keys(before), ...Object.keys(after)])];
    return fields.map((field) => ({
        field,
        before: toAuditValue(before[field]),
        after: toAuditValue(after[field]),
        changed: !sameJson(before[field], after[field]),
    }));
}

function pairText(pair: AuditDiffPair, maxLength?: number): AuditMessage {
    return auditMessage('auditLog.summary.change', {
        field: pair.field,
        from: auditValueText(pair.before, maxLength),
        to: auditValueText(pair.after, maxLength),
    });
}

/** A bioscope moved by a fish group split or relocation says so, since nobody moved it by hand. */
function movedByFishGroup(entry: AuditLogEntry): boolean {
    if (entry.action !== 'bioscope.move' || entry.metadata === null) return false;
    const source = textField(entry.metadata, 'source');
    return source !== null && source !== 'bioscope_move';
}

/** Rule 3: `before` and `after` both hold fields; the line shows what changed. */
export function summarizeDiff(entry: AuditLogEntry): AuditSummary | null {
    const before = entry.changes?.before;
    const after = entry.changes?.after;
    if (!isRecord(before) || !isRecord(after) || isEmptyRecord(before)) return null;
    const pairs = changePairs(before, after);
    const changed = pairs.filter((pair) => pair.changed);
    const summary = firstTwo(changed.map((pair) => pairText(pair, SUMMARY_VALUE_LENGTH)));
    if (summary === null) return null;
    const title = changed.map((pair) => pairText(pair));
    if (!movedByFishGroup(entry)) return { rule: 'diff', line: summary, title, pairs };
    return {
        rule: 'diff',
        line: auditMessage('auditLog.summary.viaFishGroup', { summary }),
        title: [...title, auditMessage('auditLog.summary.movedByFishGroup')],
        pairs,
    };
}

function deleteText(entry: AuditLogEntry, before: Record<string, unknown>): AuditMessage {
    if (entry.action === 'bioscope.move.undo') return auditMessage('auditLog.summary.undidMove');
    const name = textField(before, 'name') ?? textField(before, 'email');
    return name === null
        ? auditMessage('auditLog.summary.deleted')
        : auditMessage('auditLog.summary.deletedName', { name });
}

/** Rule 4: a delete keeps a snapshot of what was removed, and its name says what. */
export function summarizeDelete(entry: AuditLogEntry): AuditSummary | null {
    const before = entry.changes?.before;
    if (!isRecord(before) || entry.changes?.after !== null) return null;
    const line = deleteText(entry, before);
    return { rule: 'deleted', line, title: [line] };
}

function setText(field: string, value: unknown, maxLength?: number): AuditMessage {
    return auditMessage('auditLog.summary.set', {
        field,
        value: auditValueText(toAuditValue(value), maxLength),
    });
}

/** Rule 5: values written without a before state, such as a first placement. */
export function summarizeSet(entry: AuditLogEntry): AuditSummary | null {
    const before = entry.changes?.before;
    const after = entry.changes?.after;
    const noBefore =
        before === null || before === undefined || (isRecord(before) && isEmptyRecord(before));
    if (!isRecord(after) || !noBefore) return null;
    const fields = Object.keys(after);
    const [field] = fields;
    if (field === undefined) return null;
    const first = setText(field, after[field], SUMMARY_VALUE_LENGTH);
    const line =
        fields.length === 1
            ? first
            : auditMessage('auditLog.summary.oneMore', { first, more: fields.length - 1 });
    return { rule: 'set', line, title: fields.map((name) => setText(name, after[name])) };
}
